/* eslint-disable react/display-name */
import { CrownTwoTone } from '@ant-design/icons';
import { Popover, Space, Table, TableProps, Tag, Typography } from 'antd';
import differenceInDays from 'date-fns/differenceInDays';
import format from 'date-fns/format';
import React, { useCallback, useMemo } from 'react';
import BatteryStatus from 'src/components/atoms/BatteryStatus/BatteryStatus';
import CardStatus from 'src/components/atoms/CardsStatus';
import ConfirmButton from 'src/components/atoms/ConfirmButton';
import HeadsetWorkingMode from 'src/components/atoms/HeadsetWorkingMode';
import { Device } from 'src/features/devices/devices.types';
import { useProfile } from 'src/features/auth/auth.hooks';
import { RoleEnum, User } from 'src/features/users/users.types';
import ComponentRoleProtected from 'src/components/blocks/ComponentRoleProtected';

export interface DevicesTableProps {
  devices: Device[];
  className?: string;
  loading?: boolean;
  style?: React.CSSProperties;
  onActivateSubscription?: (device: Device) => void;
  onCancelActivateSubscription?: (device: Device) => void;
  onRemove: (device: Device) => void;
}

const DevicesTable: React.FC<DevicesTableProps> = props => {
  const { devices, loading, onRemove } = props;

  const parseDate = useCallback((date: string) => {
    if (!date) {
      return '';
    }
    return format(new Date(date), 'dd.MM HH:mm');
  }, []);

  const compareString = (a: string | null | undefined, b: string | null | undefined) => {
    const cmp = a || '-';
    return cmp.localeCompare(b || '-');
  };
  const daysFromToday = (date: string) => {
    return date && differenceInDays(new Date(), new Date(date));
  };
  const getColor = useCallback((date: string) => {
    const days = daysFromToday(date);
    if (days > 5) return 'pink';
    if (days > 2) return 'warning';
    return '';
  }, []);

  const tableConfig = useMemo<TableProps<Device>>(() => {
    const result: TableProps<Device> = {
      dataSource: devices,
      rowKey: values => values.id,
      columns: [
        {
          title: 'Метка шлема',
          dataIndex: 'label',
          key: 'label',
          sorter: (a, b) => compareString(a.label, b.label),
          render: (label: string, headset) => (
            <Space direction={'horizontal'} style={{ justifyContent: 'space-between', width: '100%' }}>
              <Space direction={'vertical'} size={0}>
                <Popover content={`ID Шлема: ${headset.phyDeviceId}`}>
                  <div>
                    <span style={{ textTransform: 'uppercase' }}>{label || '-'}</span>{' '}
                    {headset.activeSubscriptions.length > 0 && <CrownTwoTone twoToneColor='orange' />}
                  </div>
                </Popover>
                <Typography.Text type={'secondary'} style={{ fontSize: '12px' }}>
                  {headset.status?.firmware || '-'}
                </Typography.Text>
              </Space>
              <Space direction={'horizontal'}>
                <CardStatus status={headset.status} onlyWarning style={{ marginTop: 8 }} />
                {headset.hasActiveSession && <Tag color='green'>Используется</Tag>}
                {headset.isTest && <Tag color='blue'>Тестовый</Tag>}
              </Space>
            </Space>
          ),
        },
        {
          title: 'Идентификатор',
          dataIndex: 'humanId',
          key: 'humanId',
          align: 'right',
          width: 100,
          sorter: (a, b) => compareString(a.roomLabel, b.roomLabel),
          render: value => <Typography.Text style={{ textTransform: 'uppercase' }}>{value || '-'}</Typography.Text>,
        },
        {
          title: 'Комната',
          dataIndex: 'roomLabel',
          key: 'roomLabel',
          align: 'right',
          width: 100,
          sorter: (a, b) => compareString(a.roomLabel, b.roomLabel),
          render: value => <Typography.Text style={{ textTransform: 'uppercase' }}>{value || '-'}</Typography.Text>,
        },
        {
          title: 'Режим работы',
          dataIndex: 'workingMode',
          key: 'workingMode',
          align: 'right',
          width: 120,
          sorter: (a, b) => compareString(a.workingMode, b.workingMode),
          render: workingMode => <HeadsetWorkingMode workingMode={workingMode} />,
        },
        {
          title: 'Заряд',
          dataIndex: 'status',
          key: 'status?.battery ',
          align: 'right',
          width: 100,
          sorter: (a, b) => {
            const left = a.status?.battery || -1;
            const right = b.status?.battery || -1;
            return left - right;
          },
          render: (value, { status }) => <BatteryStatus status={status} />,
        },
        {
          title: 'Был в сети',
          dataIndex: 'status',
          key: 'status?.lastActivity',
          align: 'right',
          defaultSortOrder: 'descend',
          width: 200,
          sorter: (a, b) => {
            const dateA = new Date(a.status?.lastActivity).getTime();
            const dateB = new Date(b.status?.lastActivity).getTime();
            return dateA - dateB;
          },
          render: (value, { status }) => (
            <Typography.Text>
              {status?.alive ? (
                <Tag style={{ margin: 0 }} color='green'>
                  В сети
                </Tag>
              ) : (
                <Tag style={{ margin: 0 }} color={getColor(status.lastActivity)}>
                  {parseDate(status.lastActivity)}
                </Tag>
              )}
            </Typography.Text>
          ),
        },
        {
          title: 'Последняя покупка',
          dataIndex: 'lastPayment',
          key: 'lastPayment',
          align: 'right',
          width: 200,
          sorter: (a, b) => {
            const dateA = new Date(a.lastPayment).getTime();
            const dateB = new Date(b.lastPayment).getTime();
            return dateA - dateB;
          },
          render: lastPayment => (
            <Typography.Text>
              {lastPayment ? (
                <Tag style={{ margin: 0 }} color={daysFromToday(lastPayment) > 7 ? 'pink' : ''}>
                  {parseDate(lastPayment)} {}
                </Tag>
              ) : (
                '-'
              )}
            </Typography.Text>
          ),
        },
        {
          title: 'Действия',
          dataIndex: 'actions',
          key: 'actions',
          align: 'right',
          render: (value, device) => (
            <ComponentRoleProtected roles={[RoleEnum.admin]}>
              <ConfirmButton danger onClick={() => onRemove(device)}>
                Удалить
              </ConfirmButton>
            </ComponentRoleProtected>
          ),
        },
      ],
    };
    return result;
  }, [devices, getColor, parseDate, onRemove]);

  return <Table {...tableConfig} size='middle' pagination={{ pageSize: 50 }} loading={loading} />;
};

export default DevicesTable;
